import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Lock, AlertTriangle, TrendingUp, Shield, ChevronRight, Check } from 'lucide-react';
import { initiatePayment } from '@/lib/api';
import { SiteFooter } from '@/components/SiteFooter';
import { ShareReportBar } from '@/components/ShareButtons';

const SCORE_COLORS = {
  'Strong': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Viable': 'bg-blue-50 text-blue-700 border-blue-200',
  'Fragile': 'bg-amber-50 text-amber-700 border-amber-200',
  'High Risk': 'bg-orange-50 text-orange-700 border-orange-200',
  'Not Investable': 'bg-red-50 text-red-700 border-red-200',
};

const SEVERITY_COLORS = {
  'critical': 'bg-red-50 text-red-700 border-red-200',
  'major': 'bg-orange-50 text-orange-700 border-orange-200',
  'warning': 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (location.state?.report) {
      setReport(location.state.report);
    } else {
      navigate('/assess');
    }
  }, [location, navigate]);

  if (!report) return null;

  const { score, model_label, risks, confidence, execution_dependency, report_id } = report;
  const isFreeReport = report.free_report === true;
  const visibleRisks = risks?.slice(0, 3) || [];
  const hiddenRisks = (report.risks_total || risks?.length || 0) - 3;
  const isAdmin = user?.email === 'rohit.chidurala@gmail.com';
  const preview = report.financials_preview || {};

  const handleUnlock = async (plan) => {
    if (!user) {
      login();
      return;
    }
    if (isAdmin) {
      navigate(`/report/${report_id}`);
      return;
    }
    initiatePayment(plan, report_id, user, () => {
      navigate(`/report/${report_id}`);
    });
  };

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E5E5EA]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <button onClick={() => navigate('/')}><img src="https://customer-assets.emergentagent.com/job_unit-economics-3/artifacts/z3ko7m68_Cake_Studio.png" alt="ROCH" className="h-14 w-auto" /></button>
          <div className="flex items-center gap-4">
            {user ? (
              <Button data-testid="dashboard-btn" onClick={() => navigate('/dashboard')} variant="ghost" className="text-sm text-[#1D1D1F]">Dashboard</Button>
            ) : (
              <Button data-testid="signin-btn" onClick={login} variant="ghost" className="text-sm text-[#1D1D1F]">Sign In</Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-20">
        {/* Score Hero */}
        <div className="text-center mb-16" data-testid="score-display">
          <p className="text-xs md:text-sm tracking-wide uppercase font-semibold text-[#86868B] mb-4">Your Feasibility Score</p>
          <div className="relative inline-flex items-center justify-center mb-6">
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-full border-4 border-[#E5E5EA] flex items-center justify-center bg-white shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
              <div>
                <div className="text-5xl md:text-6xl font-semibold text-[#1D1D1F]" data-testid="score-value">{score?.total || 0}</div>
                <div className="text-xs text-[#86868B]">out of 100</div>
              </div>
            </div>
          </div>
          <Badge data-testid="score-label" className={`text-sm px-4 py-1.5 rounded-full border ${SCORE_COLORS[score?.label] || 'bg-gray-50 text-gray-700'}`}>
            {score?.label}
          </Badge>
          <p className="text-sm text-[#86868B] mt-4">Detected Model: <span className="text-[#1D1D1F] font-medium">{model_label}</span></p>
        </div>

        {/* Score Breakdown (Free) */}
        <div className="bg-white rounded-3xl border border-[#E5E5EA] p-8 md:p-10 mb-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="score-breakdown">
          <h3 className="text-lg font-medium text-[#1D1D1F] mb-6">Score Breakdown</h3>
          <div className="space-y-4">
            {[
              { key: 'concept_fit', label: 'Concept Fit', max: 15 },
              { key: 'financial_viability', label: 'Financial Viability', max: 30 },
              { key: 'unit_economics', label: 'Unit Economics', max: 20 },
              { key: 'operations', label: 'Operations', max: 15 },
              { key: 'founder_readiness', label: 'Founder Readiness', max: 10 },
              { key: 'risk', label: 'Risk', max: 10 },
            ].map(({ key, label, max }) => (
              <div key={key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#86868B]">{label}</span>
                  <span className="text-[#1D1D1F] font-medium">{score?.breakdown?.[key] || 0}/{max}</span>
                </div>
                <div className="h-1.5 bg-[#F5F5F7] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#1D1D1F] rounded-full transition-all duration-700"
                    style={{ width: `${((score?.breakdown?.[key] || 0) / max) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Risks (Free - show 3) */}
        <div className="bg-white rounded-3xl border border-[#E5E5EA] p-8 md:p-10 mb-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="risks-preview">
          <h3 className="text-lg font-medium text-[#1D1D1F] mb-6 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" strokeWidth={1.5} />
            Key Risks
          </h3>
          <div className="space-y-3">
            {visibleRisks.map((risk, i) => (
              <div key={i} className={`rounded-2xl border p-4 ${SEVERITY_COLORS[risk.severity]}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{risk.flag}</span>
                  <span className="text-xs uppercase font-semibold">{risk.severity}</span>
                </div>
                <p className="text-xs opacity-80">{risk.detail}</p>
              </div>
            ))}
          </div>
          {hiddenRisks > 0 && (
            <p className="text-sm text-[#1D1D1F]/40 mt-4 text-center font-medium">+ {hiddenRisks} more risks in full report</p>
          )}
        </div>

        {/* Locked Financial Indicators (Free) */}
        {isFreeReport && !isAdmin && (
          <div className="bg-white rounded-3xl border border-[#E5E5EA] p-6 md:p-8 mb-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="locked-financials">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-4 w-4 text-[#1D1D1F]/30" strokeWidth={1.5} />
              <h3 className="text-sm font-bold text-[#1D1D1F]">Financial Indicators</h3>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-[#F5F5F7] rounded-2xl p-4 text-center">
                <p className="text-sm font-bold text-[#1D1D1F]">{preview.monthly_revenue_range || '—'}</p>
                <p className="text-[10px] text-[#1D1D1F]/40 mt-1">Revenue Range</p>
              </div>
              <div className="bg-[#F5F5F7] rounded-2xl p-4 text-center">
                <p className="text-sm font-bold text-[#1D1D1F]">{preview.profitable ? 'Positive' : 'Negative'}</p>
                <p className="text-[10px] text-[#1D1D1F]/40 mt-1">Profitability</p>
              </div>
              <div className="bg-[#F5F5F7] rounded-2xl p-4 text-center">
                <p className="text-sm font-bold text-[#1D1D1F]">{preview.operating_margin_range || '—'}</p>
                <p className="text-[10px] text-[#1D1D1F]/40 mt-1">Margin Range</p>
              </div>
            </div>
            <p className="text-xs text-[#1D1D1F]/40 text-center">Exact numbers, breakeven, cost breakdown, and fix suggestions available in the full report.</p>
          </div>
        )}

        {/* Paywall / Admin Bypass */}
        {isAdmin ? (
          <div className="mb-8 text-center" data-testid="admin-bypass-section">
            <Button
              data-testid="admin-view-report-btn"
              onClick={() => navigate(`/report/${report_id}`)}
              className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-10 py-4 text-base font-medium transition-all duration-500 active:scale-95"
              size="lg"
            >
              View Full Report
              <ArrowRight className="ml-2 h-5 w-5" strokeWidth={1.5} />
            </Button>
            <p className="text-xs text-[#1D1D1F]/40 mt-3">Admin access — all features unlocked</p>
          </div>
        ) : (
          <div className="bg-[#1D1D1F] rounded-3xl p-8 md:p-10 mb-8 text-center" data-testid="paywall-section">
            <Lock className="h-8 w-8 text-white/30 mx-auto mb-4" strokeWidth={1.5} />
            <h4 className="text-xl font-bold text-white mb-2">Unlock the Full Report</h4>
            <p className="text-sm text-white/50 mb-6 max-w-md mx-auto font-medium">
              Get exact financials, breakeven analysis, cost breakdown, AI-powered fix suggestions, and scenario testing.
            </p>
            <Button
              data-testid="unlock-basic-btn"
              onClick={() => handleUnlock('basic')}
              className="bg-white text-[#1D1D1F] hover:bg-white/90 rounded-full px-8 py-3 font-semibold mb-3 transition-all duration-500 active:scale-95"
            >
              Unlock for ₹499
            </Button>
            <p className="text-xs text-white/30">Includes 1 scenario credit + full analysis</p>
          </div>
        )}

        {/* Pricing Plans */}
        <div className="mb-8" data-testid="pricing-section">
          <h3 className="text-xl md:text-2xl font-bold text-[#1D1D1F] text-center mb-2">Choose Your Plan</h3>
          <p className="text-xs text-[#1D1D1F]/40 text-center mb-6 font-medium">One-time payment per report. No subscriptions.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {[
              {
                plan: 'basic',
                name: 'Report',
                price: '499',
                tagline: 'Understand your numbers',
                features: [
                  'Complete financial breakdown',
                  'Revenue, costs & profit analysis',
                  'All risk flags with explanations',
                  'AI-powered fix suggestions',
                  'PDF report download',
                ],
              },
              {
                plan: 'scenario_pack',
                name: 'Report + Scenarios',
                price: '999',
                popular: true,
                tagline: 'Test before you commit',
                features: [
                  'Everything in Report plan',
                  '3 what-if scenario tests',
                  'Change rent, AOV, staff, food cost',
                  'Side-by-side scenario comparison',
                  'Find your best-case numbers',
                ],
              },
              {
                plan: 'pro',
                name: 'Full Analysis',
                price: '2,999',
                tagline: 'Investor-ready clarity',
                features: [
                  'Everything in Scenarios plan',
                  '5 scenario credits',
                  'Detailed cost optimization insights',
                  'Benchmark comparison with similar concepts',
                  'Priority consultation access',
                ],
              },
            ].map((tier) => (
              <div
                key={tier.plan}
                data-testid={`plan-card-${tier.plan}`}
                className={`bg-white rounded-3xl border p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)] ${
                  tier.popular ? 'border-[#1D1D1F] shadow-[0_8px_32px_rgba(0,0,0,0.04)]' : 'border-[#E5E5EA]'
                }`}
              >
                {tier.popular && (
                  <Badge className="bg-[#1D1D1F] text-white text-xs mb-3 rounded-full">Most Popular</Badge>
                )}
                <h4 className="text-base font-bold text-[#1D1D1F] mb-0.5">{tier.name}</h4>
                <p className="text-xs text-[#1D1D1F]/40 font-medium mb-3">{tier.tagline}</p>
                <div className="text-3xl font-bold text-[#1D1D1F] mb-5">₹{tier.price}</div>
                <ul className="space-y-2.5 mb-6">
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-[#1D1D1F]/70 font-medium">
                      <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`buy-${tier.plan}-btn`}
                  onClick={() => handleUnlock(tier.plan)}
                  className={`w-full rounded-full py-3 transition-all duration-500 active:scale-95 ${
                    tier.popular
                      ? 'bg-[#1D1D1F] text-white hover:bg-[#434344]'
                      : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA]'
                  }`}
                >
                  Get {tier.name}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Confidence & Execution Dependency (Free) */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white rounded-3xl border border-[#E5E5EA] p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="confidence-display">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-[#86868B]" strokeWidth={1.5} />
              <h4 className="text-sm font-medium text-[#1D1D1F]">Confidence Level</h4>
            </div>
            <div className="text-2xl font-semibold text-[#1D1D1F] capitalize">{confidence?.level || 'N/A'}</div>
            {confidence?.issues?.length > 0 && (
              <p className="text-xs text-[#86868B] mt-2">{confidence.issues.length} data quality issue(s) detected</p>
            )}
          </div>
          <div className="bg-white rounded-3xl border border-[#E5E5EA] p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="execution-dep-display">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-[#86868B]" strokeWidth={1.5} />
              <h4 className="text-sm font-medium text-[#1D1D1F]">Execution Dependency</h4>
            </div>
            <div className="text-2xl font-semibold text-[#1D1D1F] capitalize">{execution_dependency?.level || 'N/A'}</div>
          </div>
        </div>

        {/* Share */}
        <div className="mb-8">
          <ShareReportBar score={score?.total} label={score?.label} modelLabel={model_label} reportId={report_id} />
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-[#86868B]">This evaluates the business model, not execution quality.</p>
          <Button data-testid="retry-btn" onClick={() => navigate('/assess')} variant="ghost" className="text-sm text-[#0071E3] mt-4">
            Start New Assessment
          </Button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
